import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { uniqBy } from 'lodash';
import callApi, { callDownload } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_REPORT_STRUCTURES = 'FETCH_REPORT_STRUCTURES';
export const FETCH_REPORT_STRUCTURES_SUCCESS = 'FETCH_REPORT_STRUCTURES_SUCCESS';
export const FETCH_REPORT_STRUCTURES_FAIL = 'FETCH_REPORT_STRUCTURES_FAIL';
export const FETCH_REPORT_STRUCTURE = 'FETCH_REPORT_STRUCTURE';
export const FETCH_REPORT_STRUCTURE_SUCCESS = 'FETCH_REPORT_STRUCTURE_SUCCESS';
export const FETCH_REPORT_STRUCTURE_FAIL = 'FETCH_REPORT_STRUCTURE_FAIL';
export const CREATE_REPORT_STRUCTURE = 'CREATE_REPORT_STRUCTURE';
export const CREATE_REPORT_STRUCTURE_SUCCESS = 'CREATE_REPORT_STRUCTURE_SUCCESS';
export const CREATE_REPORT_STRUCTURE_FAIL = 'CREATE_REPORT_STRUCTURE_FAIL';
export const UPDATE_REPORT_STRUCTURE = 'UPDATE_REPORT_STRUCTURE';
export const UPDATE_REPORT_STRUCTURE_SUCCESS = 'UPDATE_REPORT_STRUCTURE_SUCCESS';
export const UPDATE_REPORT_STRUCTURE_FAIL = 'UPDATE_REPORT_STRUCTURE_FAIL';
export const DELETE_REPORT_STRUCTURE = 'DELETE_REPORT_STRUCTURE';
export const DELETE_REPORT_STRUCTURE_SUCCESS = 'DELETE_REPORT_STRUCTURE_SUCCESS';
export const DELETE_REPORT_STRUCTURE_FAIL = 'DELETE_REPORT_STRUCTURE_FAIL';

// Reducer
export default function reportStructuresReducer(state = { data: [], count: 0, loading: 0 }, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_REPORT_STRUCTURES:
    case FETCH_REPORT_STRUCTURE:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading + 1 } : {},
      };
    case FETCH_REPORT_STRUCTURES_SUCCESS: {
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: uniqBy(newData, 'ID'),
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_REPORT_STRUCTURES_FAIL:
    case FETCH_REPORT_STRUCTURE_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case FETCH_REPORT_STRUCTURE_SUCCESS:
    case CREATE_REPORT_STRUCTURE_SUCCESS: {
      let isPresent = false;
      let newCount = state.count;
      let newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        isPresent = true;
        return {
          ...dataItem,
          ...payload,
        };
      });
      if (!isPresent) {
        newCount += 1;
        newData = [
          payload,
          ...newData,
        ];
      }
      return {
        ...state,
        count: newCount,
        data: newData,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case UPDATE_REPORT_STRUCTURE_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_REPORT_STRUCTURE_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchReportStructures(payload) {
  return { type: FETCH_REPORT_STRUCTURES, payload };
}

export function fetchReportStructuresSuccess(payload) {
  return { type: FETCH_REPORT_STRUCTURES_SUCCESS, payload };
}

export function fetchReportStructuresFail(payload) {
  return { type: FETCH_REPORT_STRUCTURES_FAIL, payload };
}

export function fetchReportStructure(payload) {
  return { type: FETCH_REPORT_STRUCTURE, payload };
}

export function fetchReportStructureSuccess(payload) {
  return { type: FETCH_REPORT_STRUCTURE_SUCCESS, payload };
}

export function fetchReportStructureFail(payload) {
  return { type: FETCH_REPORT_STRUCTURE_FAIL, payload };
}

export function createReportStructure(payload) {
  return { type: CREATE_REPORT_STRUCTURE, payload };
}

export function createReportStructureSuccess(payload) {
  return { type: CREATE_REPORT_STRUCTURE_SUCCESS, payload };
}

export function createReportStructureFail(payload) {
  return { type: CREATE_REPORT_STRUCTURE_FAIL, payload };
}

export function updateReportStructure(payload) {
  return { type: UPDATE_REPORT_STRUCTURE, payload };
}

export function updateReportStructureSuccess(payload) {
  return { type: UPDATE_REPORT_STRUCTURE_SUCCESS, payload };
}

export function updateReportStructureFail(payload) {
  return { type: UPDATE_REPORT_STRUCTURE_FAIL, payload };
}

export function deleteReportStructure(payload) {
  return { type: DELETE_REPORT_STRUCTURE, payload };
}

export function deleteReportStructureSuccess(payload) {
  return { type: DELETE_REPORT_STRUCTURE_SUCCESS, payload };
}

export function deleteReportStructureFail(payload) {
  return { type: DELETE_REPORT_STRUCTURE_FAIL, payload };
}

// Requests
export function requestFetchReportStructures({ limit, offset }) {
  return callApi(`report/report_structure/?limit=${limit}&offset=${offset}`);
}

export function requestSearchReportStructures({ limit, offset, filters, sortBy }) {
  return callApi('report/report_structure/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'ID',
        sort: 'desc',
      },
      ...sortBy || [],
    ],
    limit,
    offset,
  });
}

export function requestFetchReportStructure(payload) {
  return callApi(`report/report_structure/?id=${payload}`);
}

export function requestCreateReportStructure(payload) {
  return callApi('report/report_structure/', 'POST', payload);
}

export function requestUpdateReportStructure(payload) {
  return callApi('report/report_structure/', 'PATCH', payload);
}

export function requestDeleteReportStructure(payload) {
  return callApi(`report/report_structure/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchReportStructuresSaga(action) {
  const { triggerLoading, filters, sortBy } = action.payload;
  try {
    const res = filters || sortBy
      ? yield call(requestSearchReportStructures, action.payload)
      : yield call(requestFetchReportStructures, action.payload);
    if (res.count === 0) {
      yield put(fetchReportStructuresSuccess({
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    } else if (action.payload.push) {
      yield put(fetchReportStructuresSuccess({
        push: true,
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    } else {
      yield put(fetchReportStructuresSuccess({
        data: res.data,
        count: res.count,
        triggerLoading,
      }));
    }
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchReportStructuresFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* fetchReportStructureSaga(action) {
  const { triggerLoading } = action.payload;
  try {
    const res = yield call(requestFetchReportStructure, action.payload.ID);
    if (res.data.ID) {
      yield put(fetchReportStructureSuccess({
        ...res.data,
        triggerLoading,
      }));
    } else {
      yield put(fetchReportStructureFail({
        triggerLoading,
      }));
    }
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchReportStructureFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createReportStructureSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateReportStructure, values);
    yield put(createReportStructureSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Report Columns Template successfully created',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createReportStructureFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateReportStructureSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateReportStructure, values);
    yield put(updateReportStructureSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Report Columns Template successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateReportStructureFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteReportStructureSaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteReportStructure, ID);
    yield put(deleteReportStructureSuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(deleteReportStructureFail(err.data));
  }
}

export function* reportStructuresSaga() {
  yield takeLatest(FETCH_REPORT_STRUCTURES, fetchReportStructuresSaga);
  yield takeEvery(FETCH_REPORT_STRUCTURE, fetchReportStructureSaga);
  yield takeEvery(CREATE_REPORT_STRUCTURE, createReportStructureSaga);
  yield takeEvery(UPDATE_REPORT_STRUCTURE, updateReportStructureSaga);
  yield takeEvery(DELETE_REPORT_STRUCTURE, deleteReportStructureSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/reportStructures.js